import { useState, useEffect } from "react";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Gift, 
  UserPlus, 
  Search,
  CheckCircle,
  CreditCard,
  Loader2
} from "lucide-react";

const AdminFreeAccess = () => {
  const [courses, setCourses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(false);
  
  const [form, setForm] = useState({
    user_email: "",
    course_id: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/admin/transactions`, { withCredentials: true })
      ]);
      setCourses(coursesRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!form.user_email || !form.course_id) {
      toast.error("Please fill in all fields");
      return;
    }

    setGranting(true);
    try {
      const response = await axios.post(`${API}/admin/grant-free-access`, {
        user_email: form.user_email,
        course_id: form.course_id
      }, { withCredentials: true });
      
      toast.success(response.data.message);
      setForm({ user_email: "", course_id: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant access");
    } finally {
      setGranting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>;
      case 'initiated':
        return <Badge className="bg-orange-100 text-orange-700">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
      default:
        return <Badge className="bg-stone-100 text-stone-700">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-free-access">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Payment Management
          </h1>
          <p className="text-stone-600 mt-1">Grant free access and view transactions</p>
        </div>

        {/* Grant Free Access */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Gift className="h-5 w-5 text-violet-700" />
              Grant Free Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Student Email *</Label>
                <Input
                  type="email"
                  value={form.user_email}
                  onChange={(e) => setForm({ ...form, user_email: e.target.value })}
                  placeholder="student@example.com"
                  className="mt-1"
                  data-testid="grant-email-input"
                />
              </div>
              <div>
                <Label>Course *</Label>
                <Select 
                  value={form.course_id} 
                  onValueChange={(v) => setForm({ ...form, course_id: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="grant-course-select">
                    <SelectValue placeholder="Select course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.course_id} value={course.course_id}>
                        {course.title} - £{(course.price || 0).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={handleGrantAccess}
                  disabled={granting}
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  data-testid="grant-access-btn"
                >
                  {granting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Granting...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Grant Free Access
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-sm text-stone-500 mt-3">
              This will create a new user (if not exists) and enroll them in the selected course for free.
            </p>
          </CardContent>
        </Card>

        {/* Transactions */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <CreditCard className="h-5 w-5 text-violet-700" />
              Recent Transactions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {transactions.length === 0 ? (
              <div className="p-12 text-center">
                <CreditCard className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-stone-900 mb-2">No transactions yet</h3>
                <p className="text-stone-500">Payment transactions will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-100">
                    <tr>
                      <th className="text-left p-4 font-medium text-stone-600">Email</th>
                      <th className="text-left p-4 font-medium text-stone-600">Course</th>
                      <th className="text-left p-4 font-medium text-stone-600">Amount</th>
                      <th className="text-left p-4 font-medium text-stone-600">Status</th>
                      <th className="text-left p-4 font-medium text-stone-600">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {transactions.map((txn) => {
                      const course = courses.find(c => c.course_id === txn.course_id);
                      return (
                        <tr key={txn.transaction_id} className="hover:bg-stone-50">
                          <td className="p-4">
                            <p className="font-medium text-stone-900">{txn.user_name}</p>
                            <p className="text-sm text-stone-500">{txn.user_email}</p>
                          </td>
                          <td className="p-4 text-stone-700">
                            {course?.title || txn.course_id}
                          </td>
                          <td className="p-4">
                            <p className="font-medium text-stone-900">
                              £{(txn.amount || 0).toFixed(2)}
                            </p>
                            {txn.promo_code && (
                              <p className="text-xs text-emerald-600">
                                {txn.promo_code} (-£{(txn.discount_amount || 0).toFixed(2)})
                              </p>
                            )}
                          </td>
                          <td className="p-4">
                            {getStatusBadge(txn.payment_status)}
                          </td>
                          <td className="p-4 text-sm text-stone-500">
                            {formatDate(txn.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminFreeAccess;
